import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  Auth,
} from 'firebase/auth';
import { getDatabase, Database } from 'firebase/database';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';

const getEnv = (key: string): string | undefined => {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.[key]) {
      return (import.meta as any).env[key];
    }
  } catch {
    // ignore
  }
  try {
    if (typeof process !== 'undefined' && process.env?.[key]) {
      return process.env[key];
    }
  } catch {
    // ignore
  }
  return undefined;
};

/**
 * Production Firebase Configuration for Adiosoft Website Builder
 * Project: adiosoft-web-ai
 */
export const firebaseConfig = {
  apiKey: getEnv('VITE_FIREBASE_API_KEY') || 'AIzaSyDjhZgErKqAtYwBH85uyj-vTNJDx0Ub9aQ',
  authDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN') || 'adiosoft-web-ai.firebaseapp.com',
  databaseURL:
    getEnv('VITE_FIREBASE_DATABASE_URL') ||
    'https://adiosoft-web-ai-default-rtdb.firebaseio.com',
  projectId: getEnv('VITE_FIREBASE_PROJECT_ID') || 'adiosoft-web-ai',
  storageBucket: getEnv('VITE_FIREBASE_STORAGE_BUCKET') || 'adiosoft-web-ai.firebasestorage.app',
  messagingSenderId: getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') || '36578695219',
  appId: getEnv('VITE_FIREBASE_APP_ID') || '1:36578695219:web:89f6853e6e00620246ab46',
  measurementId: getEnv('VITE_FIREBASE_MEASUREMENT_ID') || 'G-QLHB3DKKYJ',
};

// Guarantee single Firebase App instance initialization
export const app: FirebaseApp =
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

// Initialize Firebase Authentication safely
export const auth: Auth = getAuth(app);
if (typeof window !== 'undefined') {
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.warn('[Firebase Auth] Persistence initialization notice:', err);
  });
}

// Initialize Firebase Realtime Database
let databaseInstance: Database | null = null;
try {
  databaseInstance = getDatabase(app);
} catch (err) {
  console.warn('[Firebase RTDB] Database initialization notice:', err);
}
export const database: Database | null = databaseInstance;

// Initialize Firebase Cloud Storage
let storageInstance: FirebaseStorage | null = null;
try {
  storageInstance = getStorage(app);
} catch (err) {
  console.warn('[Firebase Storage] Storage initialization notice:', err);
}
export const storage: FirebaseStorage | null = storageInstance;

// Initialize Firebase Analytics safely (Browser & Supported environments only)
let analyticsInstance: Analytics | null = null;
if (typeof window !== 'undefined') {
  isSupported()
    .then((supported) => {
      if (supported) {
        try {
          analyticsInstance = getAnalytics(app);
        } catch (err) {
          console.warn('[Firebase Analytics] Init skipped:', err);
        }
      }
    })
    .catch(() => {
      // Analytics non-blocking fallback
    });
}
export const analytics: Analytics | null = analyticsInstance;

// Google Authentication Provider Configuration
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

// Environment configuration status helper
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.projectId &&
  firebaseConfig.projectId === 'adiosoft-web-ai'
);

// Standard aliases for modular usage across services
export {
  app as firebaseApp,
  auth as firebaseAuth,
  database as firebaseDatabase,
  storage as firebaseStorage,
  analytics as firebaseAnalytics,
};

export default app;
